import { useEffect, useRef, useState } from "react";
import api, { apiError, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Upload, Download, Pencil, Trash2, Boxes } from "lucide-react";

const empty = {
  name: "", length: 48, width: 40, height: 5, weight: 10, nesting_height: 0,
  height_limit: 96, stack_quantity_limit: 10, category: "Plastic Pallet", nmfc: "", active: true, notes: "",
};

const NUM_FIELDS = ["length", "width", "height", "weight", "nesting_height", "height_limit", "stack_quantity_limit"];

export default function Products() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const fileRef = useRef();

  const load = () => api.get("/products").then((r) => setProducts(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...empty, ...p }); setOpen(true); };

  const save = async () => {
    const payload = { ...form };
    NUM_FIELDS.forEach((f) => (payload[f] = Number(payload[f])));
    try {
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Product updated" : "Product added");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try { await api.delete(`/products/${p.id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/products/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.imported} products${data.errors.length ? `, ${data.errors.length} errors` : ""}`);
      if (data.errors.length) console.warn(data.errors);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      e.target.value = "";
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/products/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "phs_products.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tighter">Product Database</h1>
          <p className="mt-1 text-sm text-muted-foreground">{products.length} products · shipping profiles for the stack engine</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={importCsv} data-testid="import-file-input" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="import-button"><Upload className="mr-1.5 h-4 w-4" />Import CSV</Button>
          <Button variant="outline" onClick={exportCsv} data-testid="export-button"><Download className="mr-1.5 h-4 w-4" />Export</Button>
          <Button onClick={openNew} data-testid="add-product-button" className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90"><Plus className="mr-1.5 h-4 w-4" />Add Product</Button>
        </div>
      </div>

      <div className="border border-border bg-card">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">L×W×H</TableHead>
                <TableHead className="text-right">Weight</TableHead>
                <TableHead className="text-right">Nest H</TableHead>
                <TableHead className="text-right">Limit</TableHead>
                <TableHead className="text-right">Stack Qty</TableHead>
                <TableHead>NMFC</TableHead>
                <TableHead>Active</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.length === 0 && (
                <TableRow><TableCell colSpan={10} className="py-12 text-center text-muted-foreground">
                  <Boxes className="mx-auto mb-2 h-7 w-7 opacity-40" />No products. Add one or import a CSV.
                </TableCell></TableRow>
              )}
              {products.map((p) => (
                <TableRow key={p.id} className="border-border hover:bg-secondary/40" data-testid={`product-row-${p.id}`}>
                  <TableCell className="text-sm font-semibold">{p.name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{p.category}</TableCell>
                  <TableCell className="metric-num text-right text-sm">{p.length}×{p.width}×{p.height}"</TableCell>
                  <TableCell className="metric-num text-right text-sm">{p.weight} lb</TableCell>
                  <TableCell className="metric-num text-right text-sm">{p.nesting_height || "—"}</TableCell>
                  <TableCell className="metric-num text-right text-sm">{p.height_limit}"</TableCell>
                  <TableCell className="metric-num text-right text-sm">{p.stack_quantity_limit}</TableCell>
                  <TableCell className="metric-num text-sm">{p.nmfc || "—"}</TableCell>
                  <TableCell>
                    <span className={`inline-block h-2 w-2 rounded-full ${p.active ? "bg-[hsl(var(--success))]" : "bg-zinc-500"}`} />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-[hsl(var(--destructive))]" onClick={() => del(p)} data-testid={`delete-product-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="product-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="label-caps">Name</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 h-10" data-testid="product-name" />
            </div>
            {[
              ["length", "Length (in)"], ["width", "Width (in)"], ["height", "Base Height (in)"],
              ["weight", "Weight (lb)"], ["nesting_height", "Nesting Height (in)"], ["height_limit", "Height Limit (in)"],
              ["stack_quantity_limit", "Stack Qty Limit"],
            ].map(([k, label]) => (
              <div key={k}>
                <Label className="label-caps">{label}</Label>
                <Input type="number" value={form[k]} onChange={(e) => set(k, e.target.value)} className="mt-1.5 h-10" data-testid={`product-${k}`} />
              </div>
            ))}
            <div>
              <Label className="label-caps">Category</Label>
              <Input value={form.category} onChange={(e) => set("category", e.target.value)} className="mt-1.5 h-10" data-testid="product-category" />
            </div>
            <div>
              <Label className="label-caps">NMFC Item #</Label>
              <Input value={form.nmfc} onChange={(e) => set("nmfc", e.target.value)} placeholder="e.g. 150400" className="mt-1.5 h-10" data-testid="product-nmfc" />
              <p className="mt-1 text-[11px] text-muted-foreground">Item # only — the sub &amp; class are auto-derived from each shipment's density.</p>
            </div>
            <div className="col-span-2">
              <Label className="label-caps">Notes</Label>
              <Input value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1.5 h-10" />
            </div>
            <label className="col-span-2 flex items-center justify-between rounded-md border border-border px-3 py-2">
              <span className="text-sm">Active</span>
              <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="product-active" />
            </label>
          </div>
          <DialogFooter>
            <Button onClick={save} data-testid="save-product-button" className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Save Product</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
